/**
 * Glue between the Studio workspace UI and the Koko widget.
 *
 * Responsibilities:
 *  1. Builds the Anthropic tool definitions (`add_character`, etc.)
 *     that Claude can invoke to mutate StudioStore.
 *  2. Builds the per-turn system-prompt addition: workspace state
 *     summary + how-to-use-the-tools instructions + panel list.
 *  3. Routes incoming `tool_use` blocks to StudioStore mutators.
 *  4. Routes `[OPEN:panel_id]` directive tags to PanelManager.
 *
 * Exposed as a single `buildExtensions()` method so the studio
 * `main.ts` just hands the result straight to `kokoElement.setExtensions`.
 */

import type {
  AnthropicTool,
  KokoExtensions,
  ToolUseBlock,
} from "../types";
import type { KokoActionAgent } from "../agent/KokoActionAgent";
import {
  parseAndExtractDirectives,
  stripDirectivesFromStreamingText,
} from "./DirectiveTagParser";
import {
  PANEL_REGISTRY,
  type PanelManager,
  STUDIO_PANEL_IDS,
  type StudioPanelId,
} from "./PanelManager";
import type { StudioStore } from "./StudioStore";
import {
  getKokoInteractionMode,
  isToolBlockedInSuggestionMode,
  resolveToolSubmit,
} from "./KokoInteractionMode";
// Koko's editable director playbook — the source of its directing
// behavior (voice, pipeline order, rules). `director.md` ships as the
// default, but the user can edit it LIVE from the Koko panel; the store
// returns the effective text (localStorage override if present, else the
// shipped file) so edits take effect without touching code or rebuilding.
import { getEffectiveDirectorPlaybook } from "./DirectorPlaybookStore";
import { LiveActionQueue } from "./LiveActionQueue";

const VALID_MOODBOARD_KINDS = ["image", "palette", "note", "lyric"] as const;
const VALID_TIMELINE_TRACKS = ["bgm", "sfx", "dialogue", "storyboard", "video"] as const;
const KOKO_LIVE_PANEL_IDS = STUDIO_PANEL_IDS;

interface BridgeOptions {
  store: StudioStore;
  panelManager: PanelManager;
  /**
   * Action agent that drives the bird through visible UI actions
   * (fly + type + click). An absent agent makes actions fail closed:
   * silent store mutation is never a successful Koko action.
   */
  actionAgent?: KokoActionAgent | null;
  /**
   * Fired once per turn AFTER every queued choreography for that
   * turn has finished. The host wires this to KokoApp.reportBack
   * which fires a follow-up Claude call so the bird "talks back"
   * about what just got done. The argument is the list of
   * action summaries collected during the turn (one per tool
   * call) — same strings that used to flash as pills.
   */
  onTurnComplete?: (actionsCompleted: string[]) => void;
}

export class StudioKokoBridge {
  private readonly store: StudioStore;
  private readonly panelManager: PanelManager;
  private readonly actionAgent: KokoActionAgent | null;
  private readonly onTurnComplete?: (actionsCompleted: string[]) => void;
  /**
   * Action summaries collected during the current turn (e.g.
   * "+ character: mei", "~ storyboard ready"). Reset whenever a
   * new turn's directive parser runs. Drained into onTurnComplete
   * after the choreography queue empties.
   */
  private actionsCollectedThisTurn: string[] = [];
  /**
   * Bumped every time the user interrupts (ESC / slash / new
   * tool_use turn). Each turn captures its own value at start of
   * choreography; the queue's tail callback only fires the
   * report-back if the snapshot still matches.
   */
  private interruptToken = 0;

  /**
   * Serial queue of in-flight choreographies. Each tool_use enqueues
   * a promise that opens the relevant panel, flies the bird through
   * the form, and clicks submit. Multiple tool_uses in one response
   * (e.g. "add three characters") play out one after the other.
   *
   * The queue is implemented as a chain on a single promise so we
   * never run two choreographies concurrently — the bird can only
   * be in one place at a time.
   */
  private choreographyQueue: Promise<void> = Promise.resolve();

  /**
   * Set true while at least one tool_use has fired this turn (the
   * bird is now busy with a choreography). Read by
   * `parseAndStripFinalDirectives` so it can cancel pointing /
   * suppress redundant `[OPEN:...]` switches — the choreography
   * already navigates panels itself.
   */
  private didEnqueueChoreographyThisTurn = false;

  /** Visible "what Koko is doing now" overlay with pause/stop. */
  private readonly queueUI: LiveActionQueue;
  /** Resolves immediately unless the queue is paused. */
  private pauseGate: Promise<void> = Promise.resolve();
  private resolvePauseGate: (() => void) | null = null;
  /** When true, remaining queued choreographies skip (Stop pressed). */
  private stopped = false;
  /** Friendly label for the tool currently being enqueued. */
  private pendingActionLabel = "";

  constructor(options: BridgeOptions) {
    this.store = options.store;
    this.panelManager = options.panelManager;
    this.actionAgent = options.actionAgent ?? null;
    this.onTurnComplete = options.onTurnComplete;
    this.queueUI = new LiveActionQueue({
      onPause: () => this.pauseQueue(),
      onResume: () => this.resumeQueue(),
      onStop: () => this.stopQueue(),
    });
    if (typeof document !== "undefined") {
      this.queueUI.mount(document.body);
    }
  }

  buildExtensions(): KokoExtensions {
    return {
      tools: this.buildToolDefinitions(),
      buildSystemPromptAddition: () => this.buildSystemPromptAddition(),
      onToolUse: (block) => this.handleToolUse(block),
      stripDirectivesFromStreamingText: (text) =>
        stripDirectivesFromStreamingText(text),
      parseAndStripFinalDirectives: (text) => {
        const { cleanedText, panelIdsToOpen } = parseAndExtractDirectives(text);
        const choreographyJustRan = this.didEnqueueChoreographyThisTurn;
        // Reset for next turn (this hook fires exactly once per turn).
        this.didEnqueueChoreographyThisTurn = false;

        // Honor [OPEN:...] only when no choreography is running —
        // the choreography handles its own panel navigation, so a
        // late synchronous open() would yank the panel out from
        // under the bird mid-flight.
        if (panelIdsToOpen.length > 0 && !choreographyJustRan) {
          const lastPanelId = panelIdsToOpen[panelIdsToOpen.length - 1];
          this.panelManager.open(lastPanelId);
        }

        // If this turn fired a choreography, schedule a report-back
        // to fire once the queue drains. The report is gated on
        // the interrupt token: if the user presses ESC or starts a
        // new turn, the token bumps and the closure short-circuits.
        if (choreographyJustRan) {
          const tokenAtSchedule = this.interruptToken;
          this.choreographyQueue = this.choreographyQueue.then(() => {
            if (tokenAtSchedule !== this.interruptToken) return;
            const actions = this.actionsCollectedThisTurn.slice();
            this.actionsCollectedThisTurn = [];
            if (actions.length === 0) return;
            this.onTurnComplete?.(actions);
          });
        }

        return {
          cleanedText,
          deferPointingUntilNextFrame:
            panelIdsToOpen.length > 0 && !choreographyJustRan,
          cancelPointing: choreographyJustRan,
        };
      },
      onUserInterrupt: () => {
        // Bump token so any chained report-back closures see a
        // changed value and bail. Also clear the queued actions
        // so the next turn doesn't carry over stale entries, and
        // stand the live queue down.
        this.interruptToken++;
        this.actionsCollectedThisTurn = [];
        this.stopped = true;
        this.resumeQueue();
        this.queueUI.markStopped();
      },
    };
  }

  // ------------------------------------------------------------------
  // System prompt
  // ------------------------------------------------------------------

  private buildSystemPromptAddition(): string {
    const panelMenu = KOKO_LIVE_PANEL_IDS
      .map((panelId) => `  ${panelId} — ${PANEL_REGISTRY[panelId].description}`)
      .join("\n");
    const currentPanelId = this.panelManager.getCurrentPanelId() ?? "location";
    const stateSummary = this.store.buildPromptSummary();
    const interactionMode = getKokoInteractionMode();
    const modeBlock =
      interactionMode === "suggestion"
        ? [
            "INTERACTION MODE: SUGGESTION (user toggle is on suggest)",
            "you draft and recommend — you do NOT commit destructive or state-changing work automatically.",
            "- for tools with a submit parameter (characters, assets, locations, mood board, storyboard shots, image edits): ALWAYS pass submit=false. the bird types or points but never clicks a committing control — the user reviews and saves.",
            "- do NOT call destructive/blur-committing tools such as delete_*, add_scene, update_scene, or render_final_cut. describe what you'd do instead.",
            "- you MAY use [OPEN:panel_id] and jump_timeline_track to navigate the workspace.",
            "- keep spoken text brief; tell the user what you drafted and what they should approve next.",
          ].join("\n")
        : [
            "INTERACTION MODE: ACTION (user toggle is on act)",
            "you execute changes. call tools with submit=true (default) so the bird clicks buttons and commits.",
            "the bird visibly performs every tool call — types, clicks submit, toggles chips, deletes.",
          ].join("\n");

    return [
      "DIRECTOR PLAYBOOK (your editable brain — follow it):",
      getEffectiveDirectorPlaybook(),
      "",
      "STUDIO MODE",
      modeBlock,
      "",
      "you are running inside a video-production workspace. the user is making a short film. you have three capabilities beyond the normal koko behavior:",
      "",
      "1. switch panels with [OPEN:panel_id]:",
      `   valid panel_ids:`,
      panelMenu,
      `   current panel: ${currentPanelId}. only one [OPEN:...] per response.`,
      "",
      "   never combine [OPEN:...] with [POINT:...] in the same response — the screenshot you see is of the CURRENT panel, so if you switch panels those refs go stale. either point at something on the current panel (no open) or switch (no point).",
      "",
      "2. mutate workspace state with tool calls:",
      "   you can add / update / delete characters, assets, locations, scenes, and mood board pins; add storyboard shots; edit images through the shared editor; open inline Script references; navigate real Timeline clips/score; and render Final Cut. call these tools whenever the user describes supported work — don't just narrate, do it. e.g. user says 'add ali, a tired filmmaker' → add_character(name='ali', description='a tired filmmaker').",
      "",
      "   THE BIRD VISIBLY PERFORMS YOUR TOOL CALLS. it flies to the relevant panel, types into inputs character-by-character, and clicks submit. consequences:",
      "   - DO NOT add [OPEN:...] when you call a tool — the bird opens panels itself.",
      "   - DO NOT add [POINT:...] when you call a tool — the bird is busy typing.",
      "   - DO NOT use any html or markup like <cite>...</cite> in tool inputs that get typed (descriptions, scene bodies, image prompts). plain prose only — those characters end up literally in form fields.",
      "   - keep your spoken text short. one sentence like 'adding mei now' is plenty. the typing IS the show.",
      "",
      "   each tool also accepts an optional `submit: false` parameter. when the user wants you to suggest / draft (e.g. 'fill it in but let me check before saving') pass `submit: false` — the bird types into the form but doesn't click the button. user reviews and submits manually.",
      "",
      "3. complete EVERY item the user asked for in this turn:",
      "   if the user requests several supported changes, call the matching tool once per item in this turn. don't stop after two or three or ask whether to continue; the serial action queue handles the visible pacing.",
      "",
      "   chains are encouraged. Do not use [OPEN:...] alongside a tool chain because every tool navigates through its own real control.",
      "",
      interactionMode === "action"
        ? "be a director's assistant: proactive, opinionated, brief. when the user asks for help, suggest concrete next steps (add this character, draft these shots, edit this image) and then DO them, don't just describe them."
        : "be a director's assistant: proactive, opinionated, brief. in suggestion mode, draft what you can in forms and clearly state what still needs the user's click to commit.",
      "",
      "current workspace state:",
      stateSummary,
    ].join("\n");
  }

  // ------------------------------------------------------------------
  // Tool definitions
  // ------------------------------------------------------------------

  private buildToolDefinitions(): AnthropicTool[] {
    // `submit` is optional on every add/update tool. Default true =
    // bird types AND clicks the form's submit button. Pass false
    // when the user wants you to draft / suggest without committing
    // ('fill it in but let me check first').
    const submitProp = {
      submit: {
        type: "boolean",
        description:
          "Whether the bird should click the form's submit button after typing. Default: true. Pass false when the user wants you to draft/suggest only — they'll review and submit themselves.",
      },
    } as const;

    return [
      {
        name: "add_character",
        description:
          "Add a new character to the cast. Bird flies to the Characters panel, types into the form, optionally clicks Add.",
        input_schema: {
          type: "object",
          properties: {
            name: { type: "string", description: "Display name." },
            description: {
              type: "string",
              description:
                "Visual description, role, personality. Plain prose only — no html/markup. Used downstream for the portrait and storyboard.",
            },
            ...submitProp,
          },
          required: ["name", "description"],
        },
      },
      {
        name: "update_character",
        description:
          "Edit an existing character. Pass the character_id and any fields you want to change (name and/or description). Bird flies to the card, opens edit mode, retypes the chosen fields, optionally clicks Save.",
        input_schema: {
          type: "object",
          properties: {
            character_id: { type: "string" },
            name: { type: "string", description: "New name (omit to keep current)." },
            description: {
              type: "string",
              description: "New description, plain prose (omit to keep current).",
            },
            ...submitProp,
          },
          required: ["character_id"],
        },
      },
      {
        name: "delete_character",
        description:
          "Remove a character from the cast by id. Bird flies to the card and clicks its Delete button.",
        input_schema: {
          type: "object",
          properties: { character_id: { type: "string" } },
          required: ["character_id"],
        },
      },
      {
        name: "add_asset",
        description:
          "Add a prop, vehicle, set piece, wardrobe item, or misc asset to the project.",
        input_schema: {
          type: "object",
          properties: {
            name: { type: "string" },
            description: { type: "string", description: "Plain prose, no html." },
            ...submitProp,
          },
          required: ["name", "description"],
        },
      },
      {
        name: "update_asset",
        description:
          "Edit an existing asset. Pass asset_id and any fields to change.",
        input_schema: {
          type: "object",
          properties: {
            asset_id: { type: "string" },
            name: { type: "string" },
            description: { type: "string" },
            ...submitProp,
          },
          required: ["asset_id"],
        },
      },
      {
        name: "delete_asset",
        description: "Remove an asset by id. Bird flies to the card and clicks Delete.",
        input_schema: {
          type: "object",
          properties: { asset_id: { type: "string" } },
          required: ["asset_id"],
        },
      },
      {
        name: "add_location",
        description:
          "Add a shooting location. Richer descriptions (lighting, mood, layout) make the dummy preview better.",
        input_schema: {
          type: "object",
          properties: {
            name: { type: "string" },
            description: { type: "string", description: "Plain prose, no html." },
            ...submitProp,
          },
          required: ["name", "description"],
        },
      },
      {
        name: "update_location",
        description:
          "Edit an existing location. Pass location_id and any fields to change.",
        input_schema: {
          type: "object",
          properties: {
            location_id: { type: "string" },
            name: { type: "string" },
            description: { type: "string" },
            ...submitProp,
          },
          required: ["location_id"],
        },
      },
      {
        name: "delete_location",
        description: "Remove a location by id. Bird flies to the card and clicks Delete.",
        input_schema: {
          type: "object",
          properties: { location_id: { type: "string" } },
          required: ["location_id"],
        },
      },
      {
        name: "add_scene",
        description:
          "Append a new scene to the script. The bird clicks 'Add Scene' then types the body into the new scene's editor.",
        input_schema: {
          type: "object",
          properties: {
            body: {
              type: "string",
              description: "Full scene body. Plain prose, no html. Multiple lines OK.",
            },
            ...submitProp,
          },
          required: ["body"],
        },
      },
      {
        name: "update_scene",
        description:
          "Replace the body text of an existing scene. Bird flies to the scene editor and retypes it.",
        input_schema: {
          type: "object",
          properties: {
            scene_id: { type: "string" },
            body: { type: "string", description: "Plain prose, no html." },
          },
          required: ["scene_id", "body"],
        },
      },
      {
        name: "delete_scene",
        description: "Remove a scene from the script. Bird flies to the scene card and clicks Delete.",
        input_schema: {
          type: "object",
          properties: { scene_id: { type: "string" } },
          required: ["scene_id"],
        },
      },
      {
        name: "add_moodboard_item",
        description:
          "Pin a new mood board / treatment item. Bird flies to the mood board panel, fills type/title/details, and optionally clicks Pin.",
        input_schema: {
          type: "object",
          properties: {
            kind: { type: "string", enum: [...VALID_MOODBOARD_KINDS] },
            title: { type: "string", description: "Short label for the board card." },
            detail: {
              type: "string",
              description: "The style signal in plain prose (no html/markup).",
            },
            ...submitProp,
          },
          required: ["kind", "title", "detail"],
        },
      },
      {
        name: "delete_moodboard_item",
        description:
          "Remove a mood board item by id. Bird flies to that card's Remove button and clicks it.",
        input_schema: {
          type: "object",
          properties: { item_id: { type: "string" } },
          required: ["item_id"],
        },
      },
      {
        name: "jump_timeline_track",
        description:
          "Open timeline and jump via a track label or a specific scene beat. Useful for navigating into sound/dialogue/storyboard/video flows.",
        input_schema: {
          type: "object",
          properties: {
            track_id: { type: "string", enum: [...VALID_TIMELINE_TRACKS] },
            scene_id: {
              type: "string",
              description:
                "Optional: scene id whose beat should be clicked on that track. Omit to click the track label.",
            },
          },
          required: ["track_id"],
        },
      },
      {
        name: "add_storyboard_shot",
        description:
          "Add and caption a shot on a specific scene's live Storyboard surface. In suggestion mode the bird stops on + Shot for review without clicking it.",
        input_schema: {
          type: "object",
          properties: {
            scene_id: { type: "string" },
            caption: { type: "string", description: "Plain-prose shot description." },
            ...submitProp,
          },
          required: ["scene_id", "caption"],
        },
      },
      {
        name: "render_final_cut",
        description:
          "Open Final Cut, fly to the real Render film control, and render the ready storyboard.",
        input_schema: { type: "object", properties: {} },
      },
      {
        name: "open_inline_reference",
        description:
          "Open a character, location, asset, or @ reference from the live Script surface in the shared entity drawer.",
        input_schema: {
          type: "object",
          properties: {
            entity_type: { type: "string", enum: ["character", "location", "asset"] },
            entity_id: { type: "string" },
          },
          required: ["entity_type", "entity_id"],
        },
      },
      {
        name: "edit_surface_image",
        description:
          "Open the shared image editor from a real image-bearing surface, type an edit prompt, and optionally apply it.",
        input_schema: {
          type: "object",
          properties: {
            source_kind: {
              type: "string",
              enum: ["location", "character", "asset", "moodboard", "storyboard"],
            },
            source_id: { type: "string", description: "Entity, tile, or storyboard frame id." },
            prompt: { type: "string" },
            ...submitProp,
          },
          required: ["source_kind", "source_id", "prompt"],
        },
      },
    ];
  }

  // ------------------------------------------------------------------
  // Tool dispatch
  // ------------------------------------------------------------------

  /**
   * Tool dispatch. Every state-changing tool runs through the same
   * choreography queue so the bird visibly performs each action one
   * at a time. Missing DOM controls fail the queue item; direct-store
   * mutation is never accepted as a Koko success.
   *
   * Tools that take an optional `submit` parameter:
   *   - true (default) — bird types AND clicks the form's submit
   *     button (commits the change).
   *   - false — bird types but leaves the form open for the user
   *     to review and submit themselves. Used when the user asks
   *     Koko to "draft" or "suggest".
   */
  private handleToolUse(block: ToolUseBlock): void {
    const input = (block.input ?? {}) as Record<string, unknown>;
    const submit = resolveToolSubmit(readBoolean(input, "submit"));
    // Friendly label for the action-queue overlay — read by the next
    // enqueueChoreography call triggered by this tool.
    this.pendingActionLabel = describeTool(block);

    if (
      getKokoInteractionMode() === "suggestion" &&
      isToolBlockedInSuggestionMode(block.name)
    ) {
      this.fireApplied(`suggested ${block.name.replace(/_/g, " ")}`);
      return;
    }

    switch (block.name) {
      case "add_character": {
        const name = readString(input, "name");
        const description = readString(input, "description");
        if (!name || !description) return this.warnInvalid(block, "missing name or description");
        this.enqueueChoreography(() => this.choreographAddCharacter(name, description, submit));
        return;
      }
      case "update_character": {
        const id = readString(input, "character_id");
        if (!id) return this.warnInvalid(block, "missing character_id");
        const name = readString(input, "name");
        const description = readString(input, "description");
        if (name === null && description === null) {
          return this.warnInvalid(block, "no fields to update");
        }
        this.enqueueChoreography(() =>
          this.choreographUpdateCharacter(id, { name, description }, submit),
        );
        return;
      }
      case "delete_character": {
        const id = readString(input, "character_id");
        if (!id) return this.warnInvalid(block, "missing character_id");
        this.enqueueChoreography(() => this.choreographDelete("character", id));
        return;
      }
      case "add_asset": {
        const name = readString(input, "name");
        const description = readString(input, "description");
        if (!name || !description) return this.warnInvalid(block, "missing name or description");
        this.enqueueChoreography(() => this.choreographAddAsset(name, description, submit));
        return;
      }
      case "update_asset": {
        const id = readString(input, "asset_id");
        if (!id) return this.warnInvalid(block, "missing asset_id");
        const name = readString(input, "name");
        const description = readString(input, "description");
        if (name === null && description === null) {
          return this.warnInvalid(block, "no fields to update");
        }
        this.enqueueChoreography(() =>
          this.choreographUpdateAsset(id, { name, description }, submit),
        );
        return;
      }
      case "delete_asset": {
        const id = readString(input, "asset_id");
        if (!id) return this.warnInvalid(block, "missing asset_id");
        this.enqueueChoreography(() => this.choreographDelete("asset", id));
        return;
      }
      case "add_location": {
        const name = readString(input, "name");
        const description = readString(input, "description");
        if (!name || !description) return this.warnInvalid(block, "missing name or description");
        this.enqueueChoreography(() => this.choreographAddLocation(name, description, submit));
        return;
      }
      case "update_location": {
        const id = readString(input, "location_id");
        if (!id) return this.warnInvalid(block, "missing location_id");
        const name = readString(input, "name");
        const description = readString(input, "description");
        if (name === null && description === null) {
          return this.warnInvalid(block, "no fields to update");
        }
        this.enqueueChoreography(() =>
          this.choreographUpdateLocation(id, { name, description }, submit),
        );
        return;
      }
      case "delete_location": {
        const id = readString(input, "location_id");
        if (!id) return this.warnInvalid(block, "missing location_id");
        this.enqueueChoreography(() => this.choreographDelete("location", id));
        return;
      }
      case "add_scene": {
        const body = readString(input, "body");
        if (!body) return this.warnInvalid(block, "missing body");
        this.enqueueChoreography(() => this.choreographAddScene(body));
        return;
      }
      case "update_scene": {
        const id = readString(input, "scene_id");
        const body = readString(input, "body");
        if (!id || body === null) return this.warnInvalid(block, "missing scene_id or body");
        this.enqueueChoreography(() => this.choreographUpdateScene(id, body));
        return;
      }
      case "delete_scene": {
        const id = readString(input, "scene_id");
        if (!id) return this.warnInvalid(block, "missing scene_id");
        this.enqueueChoreography(() => this.choreographDelete("scene", id));
        return;
      }
      case "set_costume": {
        this.rejectUnsupportedTool(
          block,
          "Costume is a dead/stale surface and is not part of the deployed studio",
        );
        return;
      }
      case "cast_scene": {
        this.rejectUnsupportedTool(
          block,
          "casting is derived from the Script; use visible inline character references",
        );
        return;
      }
      case "set_storyboard_status": {
        this.rejectUnsupportedTool(
          block,
          "storyboard readiness is derived from real generated frames; use add_storyboard_shot",
        );
        return;
      }
      case "add_moodboard_item": {
        const kind = readString(input, "kind");
        const title = readString(input, "title");
        const detail = readString(input, "detail");
        if (!kind || !title || !detail) {
          return this.warnInvalid(block, "missing kind, title, or detail");
        }
        if (!VALID_MOODBOARD_KINDS.includes(kind as (typeof VALID_MOODBOARD_KINDS)[number])) {
          return this.warnInvalid(block, `invalid kind: ${kind}`);
        }
        this.enqueueChoreography(() =>
          this.choreographAddMoodBoardItem(
            kind as (typeof VALID_MOODBOARD_KINDS)[number],
            title,
            detail,
            submit,
          ),
        );
        return;
      }
      case "delete_moodboard_item": {
        const id = readString(input, "item_id");
        if (!id) return this.warnInvalid(block, "missing item_id");
        this.enqueueChoreography(() => this.choreographDeleteMoodBoardItem(id));
        return;
      }
      case "update_sound_design": {
        this.rejectUnsupportedTool(
          block,
          "sound moved to Script line controls and Timeline score controls",
        );
        return;
      }
      case "set_scene_camera": {
        this.rejectUnsupportedTool(
          block,
          "camera is a per-storyboard-frame modal; scene-level direct mutation is forbidden",
        );
        return;
      }
      case "jump_timeline_track": {
        const trackId = readString(input, "track_id");
        const sceneId = readString(input, "scene_id");
        if (
          !trackId ||
          !VALID_TIMELINE_TRACKS.includes(trackId as (typeof VALID_TIMELINE_TRACKS)[number])
        ) {
          return this.warnInvalid(block, `invalid track_id: ${trackId}`);
        }
        this.enqueueChoreography(() =>
          this.choreographJumpTimelineTrack(
            trackId as (typeof VALID_TIMELINE_TRACKS)[number],
            sceneId,
          ),
        );
        return;
      }
      case "add_storyboard_shot": {
        const sceneId = readString(input, "scene_id");
        const caption = readString(input, "caption");
        if (!sceneId || caption === null) {
          return this.warnInvalid(block, "missing scene_id or caption");
        }
        this.enqueueChoreography(() =>
          this.choreographAddStoryboardShot(sceneId, caption, submit),
        );
        return;
      }
      case "render_final_cut": {
        this.enqueueChoreography(() => this.choreographRenderFinalCut());
        return;
      }
      case "open_inline_reference": {
        const entityType = readString(input, "entity_type");
        const entityId = readString(input, "entity_id");
        if (
          !entityId ||
          (entityType !== "character" && entityType !== "location" && entityType !== "asset")
        ) {
          return this.warnInvalid(block, "invalid entity_type or missing entity_id");
        }
        this.enqueueChoreography(() =>
          this.choreographOpenInlineReference(entityType, entityId),
        );
        return;
      }
      case "edit_surface_image": {
        const sourceKind = readString(input, "source_kind");
        const sourceId = readString(input, "source_id");
        const prompt = readString(input, "prompt");
        if (
          !sourceId ||
          prompt === null ||
          (sourceKind !== "location" &&
            sourceKind !== "character" &&
            sourceKind !== "asset" &&
            sourceKind !== "moodboard" &&
            sourceKind !== "storyboard")
        ) {
          return this.warnInvalid(block, "invalid source_kind or missing source_id/prompt");
        }
        this.enqueueChoreography(() =>
          this.choreographEditSurfaceImage(sourceKind, sourceId, prompt, submit),
        );
        return;
      }
      case "set_project_title": {
        this.rejectUnsupportedTool(
          block,
          "the current title control uses a native prompt and has no visible field contract",
        );
        return;
      }
      default: {
        console.warn(`[studio] unhandled tool name: ${block.name}`, block);
      }
    }
  }

  // ------------------------------------------------------------------
  // Choreography queue
  // ------------------------------------------------------------------

  /**
   * Append an async choreography to the serial queue. The queue
   * runs jobs one at a time (the bird can only be in one place),
   * but enqueueing is non-blocking — `handleToolUse` returns
   * synchronously to keep the SSE stream parser flowing.
   */
  private enqueueChoreography(asyncJob: () => Promise<void>): void {
    // First enqueue of a new turn clears any prior Stop so the bird works again.
    if (!this.didEnqueueChoreographyThisTurn) this.stopped = false;
    this.didEnqueueChoreographyThisTurn = true;

    // Register this action in the live queue overlay (queued → doing → done).
    const actionLabel = this.pendingActionLabel || "working…";
    const itemId = this.queueUI.add(actionLabel);

    // Gate each job on the pause/stop controls before it runs.
    const runGated = async (): Promise<void> => {
      if (this.stopped) {
        this.queueUI.setStatus(itemId, "skipped");
        return;
      }
      await this.pauseGate;
      if (this.stopped) {
        this.queueUI.setStatus(itemId, "skipped");
        return;
      }
      this.queueUI.setStatus(itemId, "doing");
      try {
        this.emitChoreographyEvent("doing", actionLabel);
        await asyncJob();
        this.queueUI.setStatus(itemId, "done");
        this.emitChoreographyEvent("done", actionLabel);
      } catch (error) {
        this.queueUI.setStatus(itemId, "failed");
        this.emitChoreographyEvent(
          "failed",
          actionLabel,
          error instanceof Error ? error.message : String(error),
        );
        throw error;
      }
    };

    if (!this.actionAgent) {
      this.queueUI.setStatus(itemId, "failed");
      this.emitChoreographyEvent(
        "failed",
        actionLabel,
        "Koko action agent unavailable",
      );
      console.warn("[studio] choreography refused: Koko action agent unavailable");
      return;
    }
    const agent = this.actionAgent;
    this.choreographyQueue = this.choreographyQueue.then(async () => {
      agent.beginChoreography();
      try {
        await runGated();
      } catch (error) {
        console.warn("[studio] choreography error:", error);
      } finally {
        agent.endChoreography();
      }
    });
  }

  private emitChoreographyEvent(
    phase: "doing" | "done" | "failed",
    action: string,
    reason?: string,
  ): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("koko:choreography", {
        detail: { phase, action, ...(reason ? { reason } : {}) },
      }),
    );
  }

  /** Pause the choreography queue — the in-flight job finishes, the rest wait. */
  private pauseQueue(): void {
    if (this.resolvePauseGate) return; // already paused
    this.pauseGate = new Promise<void>((resolve) => {
      this.resolvePauseGate = resolve;
    });
  }

  private resumeQueue(): void {
    this.resolvePauseGate?.();
    this.resolvePauseGate = null;
    this.pauseGate = Promise.resolve();
  }

  /** Stop: remaining queued actions skip; the bird stands down. */
  private stopQueue(): void {
    this.stopped = true;
    this.resumeQueue();
    this.interruptToken++;
    this.actionsCollectedThisTurn = [];
  }

  // ------------------------------------------------------------------
  // Choreographers — each opens the relevant panel, types into the
  // form, clicks submit, and confirms live state. Any missing target
  // fails closed so silent state mutation cannot masquerade as success.
  // ------------------------------------------------------------------

  private async choreographAddCharacter(
    name: string,
    description: string,
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("characters");
      const addButton = this.queryAction<HTMLButtonElement>("character-add");
      if (!addButton) throw new Error("character Add control not found");
      await agent.flyTo(addButton);
      await agent.click(addButton);
      await agent.waitForPaint();
      const nameInput = this.queryAction<HTMLInputElement>("character-name");
      const descInput = this.queryAction<HTMLTextAreaElement>("character-description");
      const submitButton = this.queryAction<HTMLButtonElement>("character-submit");
      if (!nameInput || !descInput || !submitButton) {
        throw new Error("character creator inputs not found");
      }
      await agent.flyTo(nameInput);
      await agent.typeInto(nameInput, name);
      await agent.flyTo(descInput);
      await agent.typeInto(descInput, description);
      if (submit) {
        await agent.flyTo(submitButton);
        await agent.click(submitButton);
        const created = this.store.get("characters").find((item) => item.name === name);
        if (!created) throw new Error("submitted character was not persisted");
        await this.flyToVisibleConfirmation(created.id);
        this.fireApplied(`+ character: ${name}`);
      } else {
        this.fireApplied(`drafted ${name} — review + submit`);
      }
    } catch (error) {
      this.choreographyFailed("add_character", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographUpdateCharacter(
    characterId: string,
    patch: { name: string | null; description: string | null },
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("characters");
      const editButton = this.queryActionForId<HTMLButtonElement>(
        "character-edit",
        characterId,
      );
      if (!editButton) {
        throw new Error(`character ${characterId} edit button not found`);
      }
      await agent.flyTo(editButton);
      await agent.click(editButton);
      await agent.waitForPaint();

      const modalRoot = this.queryOpenModal("character-edit");
      if (!modalRoot) throw new Error("character edit modal did not open");

      if (patch.name !== null) {
        const nameInput = this.queryModalActionForId<HTMLInputElement>(
          modalRoot,
          "character-edit-name",
          characterId,
        );
        if (!nameInput) throw new Error("character edit name field not found");
        await agent.flyTo(nameInput);
        await agent.typeInto(nameInput, patch.name);
      }
      if (patch.description !== null) {
        const descInput = this.queryModalActionForId<HTMLTextAreaElement>(
          modalRoot,
          "character-edit-description",
          characterId,
        );
        if (!descInput) throw new Error("character edit description field not found");
        await agent.flyTo(descInput);
        await agent.typeInto(descInput, patch.description);
      }

      if (submit) {
        const saveButton = this.queryModalFooterActionForId<HTMLButtonElement>(
          modalRoot,
          "character-edit-save",
          characterId,
        );
        if (!saveButton) throw new Error("character modal save control not found");
        await agent.flyTo(saveButton);
        await agent.click(saveButton);
        await this.waitForCondition(
          () => !modalRoot.isConnected,
          "character edit modal to close",
          30,
        );
        this.fireApplied(`~ character ${patch.name ?? characterId}`);
      } else {
        this.fireApplied(`drafted edit on ${characterId}`);
      }
    } catch (error) {
      this.choreographyFailed("update_character", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographAddAsset(
    name: string,
    description: string,
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("assets");
      const addButton = this.queryAction<HTMLButtonElement>("asset-add");
      if (!addButton) throw new Error("asset Add control not found");
      await agent.flyTo(addButton);
      await agent.click(addButton);
      await agent.waitForPaint();
      const nameInput = this.queryAction<HTMLInputElement>("asset-name");
      const descInput = this.queryAction<HTMLTextAreaElement>("asset-description");
      const submitButton = this.queryAction<HTMLButtonElement>("asset-submit");
      if (!nameInput || !descInput || !submitButton) {
        throw new Error("asset form inputs not found");
      }
      await agent.flyTo(nameInput);
      await agent.typeInto(nameInput, name);
      await agent.flyTo(descInput);
      await agent.typeInto(descInput, description);
      if (submit) {
        await agent.flyTo(submitButton);
        await agent.click(submitButton);
        const created = this.store.get("assets").find((item) => item.name === name);
        if (!created) throw new Error("submitted asset was not persisted");
        await this.flyToVisibleConfirmation(created.id);
        this.fireApplied(`+ asset: ${name}`);
      } else {
        this.fireApplied(`drafted asset ${name}`);
      }
    } catch (error) {
      this.choreographyFailed("add_asset", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographUpdateAsset(
    assetId: string,
    patch: {
      name: string | null;
      description: string | null;
    },
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("assets");
      const editButton = this.queryActionForId<HTMLButtonElement>(
        "asset-edit",
        assetId,
      );
      if (!editButton) throw new Error(`asset ${assetId} edit button not found`);
      await agent.flyTo(editButton);
      await agent.click(editButton);
      await agent.waitForPaint();

      const modalRoot = this.queryOpenModal("asset-edit");
      if (!modalRoot) throw new Error("asset edit modal did not open");

      if (patch.name !== null) {
        const nameInput = this.queryModalActionForId<HTMLInputElement>(
          modalRoot,
          "asset-edit-name",
          assetId,
        );
        if (!nameInput) throw new Error("asset edit name field not found");
        await agent.flyTo(nameInput);
        await agent.typeInto(nameInput, patch.name);
      }
      if (patch.description !== null) {
        const descInput = this.queryModalActionForId<HTMLTextAreaElement>(
          modalRoot,
          "asset-edit-description",
          assetId,
        );
        if (!descInput) throw new Error("asset edit description field not found");
        await agent.flyTo(descInput);
        await agent.typeInto(descInput, patch.description);
      }
      if (submit) {
        const saveButton = this.queryModalFooterActionForId<HTMLButtonElement>(
          modalRoot,
          "asset-edit-save",
          assetId,
        );
        if (!saveButton) throw new Error("asset modal save control not found");
        await agent.flyTo(saveButton);
        await agent.click(saveButton);
        await this.waitForCondition(
          () => !modalRoot.isConnected,
          "asset edit modal to close",
          30,
        );
        this.fireApplied(`~ asset ${patch.name ?? assetId}`);
      } else {
        this.fireApplied(`drafted edit on ${assetId}`);
      }
    } catch (error) {
      this.choreographyFailed("update_asset", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographAddLocation(
    name: string,
    description: string,
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("location");
      const addButton = this.queryAction<HTMLButtonElement>("location-add");
      if (!addButton) throw new Error("location Add control not found");
      await agent.flyTo(addButton);
      await agent.click(addButton);
      await agent.waitForPaint();
      const nameInput = this.queryAction<HTMLInputElement>("location-name");
      const descInput = this.queryAction<HTMLTextAreaElement>("location-description");
      const submitButton = this.queryAction<HTMLButtonElement>("location-submit");
      if (!nameInput || !descInput || !submitButton) {
        throw new Error("location form inputs not found");
      }
      await agent.flyTo(nameInput);
      await agent.typeInto(nameInput, name);
      await agent.flyTo(descInput);
      await agent.typeInto(descInput, description);
      if (submit) {
        await agent.flyTo(submitButton);
        await agent.click(submitButton);
        const created = this.store.get("locations").find((item) => item.name === name);
        if (!created) throw new Error("submitted location was not persisted");
        await this.flyToVisibleConfirmation(created.id);
        this.fireApplied(`+ location: ${name}`);
      } else {
        this.fireApplied(`drafted location ${name}`);
      }
    } catch (error) {
      this.choreographyFailed("add_location", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographUpdateLocation(
    locationId: string,
    patch: { name: string | null; description: string | null },
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("location");
      const editButton = this.queryActionForId<HTMLButtonElement>(
        "location-edit",
        locationId,
      );
      if (!editButton) throw new Error(`location ${locationId} edit button not found`);
      await agent.flyTo(editButton);
      await agent.click(editButton);
      await agent.waitForPaint();

      const modalRoot = this.queryOpenModal("location-edit");
      if (!modalRoot) throw new Error("location edit modal did not open");

      if (patch.name !== null) {
        const nameInput = this.queryModalActionForId<HTMLInputElement>(
          modalRoot,
          "location-edit-name",
          locationId,
        );
        if (!nameInput) throw new Error("location edit name field not found");
        await agent.flyTo(nameInput);
        await agent.typeInto(nameInput, patch.name);
      }
      if (patch.description !== null) {
        const descInput = this.queryModalActionForId<HTMLTextAreaElement>(
          modalRoot,
          "location-edit-description",
          locationId,
        );
        if (!descInput) throw new Error("location edit description field not found");
        await agent.flyTo(descInput);
        await agent.typeInto(descInput, patch.description);
      }
      if (submit) {
        const saveButton = this.queryModalFooterActionForId<HTMLButtonElement>(
          modalRoot,
          "location-edit-save",
          locationId,
        );
        if (!saveButton) throw new Error("location modal save control not found");
        await agent.flyTo(saveButton);
        await agent.click(saveButton);
        await this.waitForCondition(
          () => !modalRoot.isConnected,
          "location edit modal to close",
          30,
        );
        this.fireApplied(`~ location ${patch.name ?? locationId}`);
      } else {
        this.fireApplied(`drafted edit on ${locationId}`);
      }
    } catch (error) {
      this.choreographyFailed("update_location", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographAddScene(body: string): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("script_editor");
      const beforeIds = new Set(this.store.get("scenes").map((scene) => scene.id));
      const initialLine = this.queryAction<HTMLElement>("script-pending-line");
      const sceneMenus = Array.from(
        document.querySelectorAll<HTMLButtonElement>('[data-koko-action="scene-menu"]'),
      );
      const sceneMenu = sceneMenus[sceneMenus.length - 1] ?? null;
      if (sceneMenu) {
        await agent.flyTo(sceneMenu);
        await agent.click(sceneMenu);
        await agent.waitForPaint();
        const addSceneButton = sceneMenu.parentElement?.querySelector<HTMLButtonElement>(
          '[data-koko-action="scene-menu-add"]',
        );
        if (!addSceneButton) throw new Error("contextual Add scene control not found");
        await agent.flyTo(addSceneButton);
        await agent.click(addSceneButton);
        await agent.waitForPaint();
        const newScene = this.store.get("scenes").find((scene) => !beforeIds.has(scene.id));
        if (!newScene) throw new Error("Add scene control did not create a scene");
        const newSceneBody = document.querySelector<HTMLElement>(
          `[data-koko-action="script-line"][data-scene-id="${cssEscape(newScene.id)}"]`,
        );
        if (!newSceneBody) throw new Error("new scene editor did not paint after Add scene");
        await agent.flyTo(newSceneBody);
        await agent.typeInto(newSceneBody, body);
      } else if (initialLine) {
        // Empty-document path: the real pending line creates the first scene
        // on blur, so typing it is both the visible field entry and submit.
        await agent.flyTo(initialLine);
        await agent.typeInto(initialLine, body);
      } else {
        throw new Error("neither Add scene nor the empty-document line is available");
      }
      await this.waitForCondition(
        () => this.store.get("scenes").some((scene) => scene.body.includes(body)),
        "scene body to persist",
      );
      this.fireApplied(`+ scene`);
    } catch (error) {
      this.choreographyFailed("add_scene", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographUpdateScene(
    sceneId: string,
    body: string,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("script_editor");
      const sceneBody = document.querySelector<HTMLElement>(
        `[data-koko-action="script-line"][data-scene-id="${cssEscape(sceneId)}"]`,
      );
      if (!sceneBody) {
        throw new Error(`scene body for ${sceneId} not found`);
      }
      await agent.flyTo(sceneBody);
      await agent.typeInto(sceneBody, body);
      await this.waitForCondition(
        () => this.store.get("scenes").some((scene) => scene.id === sceneId && scene.body.includes(body)),
        "scene edit to persist",
      );
      this.fireApplied(`~ scene ${sceneId}`);
    } catch (error) {
      this.choreographyFailed("update_scene", error);
    } finally {
      agent.perch();
    }
  }

  /**
   * Universal "fly to a card's delete button and click it" handler.
   * Works for any kind of card whose delete button is tagged with
   * `data-koko-action="${kind}-delete" data-id="${id}"`.
   */
  private async choreographDelete(
    kind: "character" | "asset" | "location" | "scene",
    id: string,
  ): Promise<void> {
    const agent = this.actionAgent;
    const panelByKind: Record<typeof kind, StudioPanelId> = {
      character: "characters",
      asset: "assets",
      location: "location",
      scene: "script_editor",
    };
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle(panelByKind[kind]);
      if (kind === "scene") {
        const menu = this.queryActionForId<HTMLButtonElement>("scene-menu", id);
        if (!menu) throw new Error(`scene ${id} menu not found`);
        await agent.flyTo(menu);
        await agent.click(menu);
        await agent.waitForPaint();
        const request = menu.parentElement?.querySelector<HTMLButtonElement>(
          '[data-koko-action="scene-delete-request"]',
        );
        if (!request) throw new Error(`scene ${id} delete request not found`);
        await agent.flyTo(request);
        await agent.click(request);
        await agent.waitForPaint();
        const confirm = menu.parentElement?.querySelector<HTMLButtonElement>(
          '[data-koko-action="scene-delete-confirm"]',
        );
        if (!confirm) throw new Error(`scene ${id} delete confirmation not found`);
        await agent.flyTo(confirm);
        await agent.click(confirm);
        this.fireApplied(`– scene ${id}`);
        return;
      }
      const deleteButton = this.queryActionForId<HTMLButtonElement>(
        `${kind}-delete`,
        id,
      );
      if (!deleteButton) {
        throw new Error(`${kind} ${id} delete button not found`);
      }
      await agent.flyTo(deleteButton);
      await agent.click(deleteButton);
      this.fireApplied(`– ${kind} ${id}`);
    } catch (error) {
      this.choreographyFailed(`delete_${kind}`, error);
    } finally {
      agent.perch();
    }
  }

  private async choreographAddMoodBoardItem(
    kind: (typeof VALID_MOODBOARD_KINDS)[number],
    title: string,
    detail: string,
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("moodboard");
      const addButton = this.queryAction<HTMLButtonElement>("moodboard-add");
      if (!addButton) throw new Error("mood board Add control not found");
      await agent.flyTo(addButton);
      await agent.click(addButton);
      await agent.waitForPaint();
      const kindInput = this.queryAction<HTMLSelectElement>("moodboard-kind");
      const titleInput = this.queryAction<HTMLInputElement>("moodboard-title");
      const detailInput = this.queryAction<HTMLTextAreaElement>("moodboard-detail");
      const submitButton = this.queryAction<HTMLButtonElement>("moodboard-submit");
      if (!kindInput || !titleInput || !detailInput || !submitButton) {
        throw new Error("mood board form inputs not found");
      }
      await agent.flyTo(kindInput);
      kindInput.value = kind;
      kindInput.dispatchEvent(new Event("change", { bubbles: true }));
      await agent.flyTo(titleInput);
      await agent.typeInto(titleInput, title);
      await agent.flyTo(detailInput);
      await agent.typeInto(detailInput, detail);
      if (submit) {
        await agent.flyTo(submitButton);
        await agent.click(submitButton);
        const created = this.store.get("moodBoardItems").find((item) => item.title === title);
        if (!created) throw new Error("submitted mood board item was not persisted");
        await this.flyToVisibleConfirmation(created.id);
        this.fireApplied(`+ mood board item: ${title}`);
      } else {
        this.fireApplied(`drafted mood board item ${title}`);
      }
    } catch (error) {
      this.choreographyFailed("add_moodboard_item", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographDeleteMoodBoardItem(itemId: string): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("moodboard");
      const deleteButton = this.queryActionForId<HTMLButtonElement>(
        "moodboard-remove",
        itemId,
      );
      if (!deleteButton) {
        throw new Error(`moodboard item ${itemId} remove button not found`);
      }
      await agent.flyTo(deleteButton);
      await agent.click(deleteButton);
      this.fireApplied(`– mood board ${itemId}`);
    } catch (error) {
      this.choreographyFailed("delete_moodboard_item", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographAddStoryboardShot(
    sceneId: string,
    caption: string,
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("storyboard");
      const addButton = this.queryActionForId<HTMLButtonElement>(
        "storyboard-add-frame",
        sceneId,
      );
      if (!addButton) throw new Error(`+ Shot control for scene ${sceneId} not found`);
      await agent.flyTo(addButton);
      const beforeIds = new Set(
        (this.store.get("storyboardFramesBySceneId")[sceneId] ?? []).map((frame) => frame.id),
      );
      await agent.click(addButton);
      await agent.waitForPaint();
      const captionField = this.queryAction<HTMLTextAreaElement>("storyboard-new-caption");
      const saveButton = this.queryAction<HTMLButtonElement>("storyboard-create-save");
      if (!captionField || !saveButton) {
        throw new Error("storyboard Add shot modal fields not found");
      }
      await agent.flyTo(captionField);
      await agent.typeInto(captionField, caption);
      if (!submit) {
        this.fireApplied(`drafted storyboard shot for ${sceneId}`);
        return;
      }
      await agent.flyTo(saveButton);
      await agent.click(saveButton);
      await agent.waitForPaint();
      const frame = (this.store.get("storyboardFramesBySceneId")[sceneId] ?? []).find(
        (candidate) => !beforeIds.has(candidate.id),
      );
      if (!frame) throw new Error("Add shot modal did not create a storyboard frame");
      await this.waitForCondition(
        () =>
          (this.store.get("storyboardFramesBySceneId")[sceneId] ?? []).some(
            (candidate) => candidate.id === frame.id && candidate.caption === caption,
          ),
        "storyboard caption to persist",
      );
      await this.flyToVisibleConfirmation(frame.id);
      this.fireApplied(`+ storyboard shot for ${sceneId}`);
    } catch (error) {
      this.choreographyFailed("add_storyboard_shot", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographRenderFinalCut(): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("final_video");
      const renderButton = this.queryAction<HTMLButtonElement>("render-video");
      if (!renderButton) throw new Error("Render film control not found");
      await agent.flyTo(renderButton);
      await agent.click(renderButton);
      await this.waitForCondition(
        () => this.store.get("videoStatus") === "rendering" || this.store.get("videoStatus") === "ready",
        "Final Cut render state",
      );
      this.fireApplied("rendering final cut");
    } catch (error) {
      this.choreographyFailed("render_final_cut", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographOpenInlineReference(
    entityType: "character" | "location" | "asset",
    entityId: string,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("script_editor");
      const actionByType = {
        character: "open-character",
        location: "script-open-location",
        asset: "",
      } as const;
      const action = actionByType[entityType];
      const target =
        (action
          ? document.querySelector<HTMLElement>(
              `[data-koko-action="${action}"][data-id="${cssEscape(entityId)}"]`,
            )
          : null) ??
        document.querySelector<HTMLElement>(
          `.fd-ref-pill[data-ref-type="${entityType}"][data-entity-id="${cssEscape(entityId)}"]`,
        );
      if (!target) throw new Error(`inline ${entityType} reference ${entityId} not found`);
      await agent.flyTo(target);
      await agent.click(target);
      await this.waitForCondition(
        () => document.querySelector('.studio-drawer-root[data-open="true"]') !== null,
        "shared entity drawer to open",
      );
      this.fireApplied(`opened inline ${entityType} ${entityId}`);
    } catch (error) {
      this.choreographyFailed("open_inline_reference", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographEditSurfaceImage(
    sourceKind: "location" | "character" | "asset" | "moodboard" | "storyboard",
    sourceId: string,
    prompt: string,
    submit: boolean,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    const panelBySource: Record<typeof sourceKind, StudioPanelId> = {
      location: "location",
      character: "characters",
      asset: "assets",
      moodboard: "moodboard",
      storyboard: "storyboard",
    };
    try {
      await this.openPanelAndSettle(panelBySource[sourceKind]);
      if (sourceKind === "character") {
        const card = document.querySelector<HTMLElement>(
          `[data-koko-action="character-edit"][data-id="${cssEscape(sourceId)}"]`,
        );
        if (!card) throw new Error(`character image source ${sourceId} not found`);
        await agent.flyTo(card);
        await agent.click(card);
        await agent.waitForPaint();
        const wardrobe = this.queryAction<HTMLElement>("character-tab-wardrobe");
        if (!wardrobe) throw new Error("character Wardrobe tab not found");
        await agent.flyTo(wardrobe);
        await agent.click(wardrobe);
        await agent.waitForPaint();
      }
      const escaped = cssEscape(sourceId);
      const sourceTarget =
        sourceKind === "storyboard"
          ? document.querySelector<HTMLElement>(
              `.sb-card[data-frame-id="${escaped}"] [data-koko-action="storyboard-edit-image"]`,
            )
          : sourceKind === "character"
            ? this.queryAction<HTMLElement>("character-primary-image-edit")
            : document.querySelector<HTMLElement>(
                `[data-koko-action="${
                  sourceKind === "location"
                    ? "location-plate-edit"
                    : sourceKind === "asset"
                      ? "asset-image-edit"
                      : `${sourceKind}-edit-image`
                }"][data-id="${escaped}"]`,
              );
      if (!sourceTarget) throw new Error(`${sourceKind} image Edit control ${sourceId} not found`);
      await agent.flyTo(sourceTarget);
      await agent.click(sourceTarget);
      await this.waitForCondition(
        () => document.querySelector('[data-koko-surface="image-editor"][data-open="true"]') !== null,
        "shared image editor to open",
      );
      const promptField = this.queryAction<HTMLTextAreaElement>("image-edit-prompt");
      const applyButton = this.queryAction<HTMLButtonElement>("image-edit-apply");
      if (!promptField || !applyButton) throw new Error("shared image editor fields not found");
      await agent.flyTo(promptField);
      await agent.typeInto(promptField, prompt);
      if (submit) {
        await agent.flyTo(applyButton);
        await agent.click(applyButton);
        await this.waitForCondition(
          () => document.querySelector(".studio-image-editor")?.getAttribute("data-busy") === "false",
          "image edit to apply",
          180,
        );
        this.fireApplied(`edited ${sourceKind} image ${sourceId}`);
      } else {
        this.fireApplied(`drafted ${sourceKind} image edit ${sourceId}`);
      }
    } catch (error) {
      this.choreographyFailed("edit_surface_image", error);
    } finally {
      agent.perch();
    }
  }

  private async choreographJumpTimelineTrack(
    trackId: (typeof VALID_TIMELINE_TRACKS)[number],
    sceneId: string | null,
  ): Promise<void> {
    const agent = this.actionAgent;
    if (!agent) throw new Error("Koko action agent unavailable");
    try {
      await this.openPanelAndSettle("timeline");
      const kindByTrack: Partial<Record<(typeof VALID_TIMELINE_TRACKS)[number], string>> = {
        sfx: "sfx",
        dialogue: "audio",
        storyboard: "video",
        video: "video",
      };
      const kind = kindByTrack[trackId];
      const scenePart = sceneId ? `[data-scene-id="${cssEscape(sceneId)}"]` : "";
      const target =
        trackId === "bgm"
          ? this.queryAction<HTMLElement>("timeline-score-style")
          : kind
            ? document.querySelector<HTMLElement>(
                `[data-koko-action="timeline-clip"][data-kind="${kind}"]${scenePart}`,
              )
            : null;
      if (!target) throw new Error(`live ${trackId} timeline target not found`);
      await agent.flyTo(target);
      await agent.click(target);
      this.fireApplied(`opened ${trackId} timeline track`);
    } catch (error) {
      this.choreographyFailed("jump_timeline_track", error);
    } finally {
      agent.perch();
    }
  }

  // ------------------------------------------------------------------
  // Choreography helpers
  // ------------------------------------------------------------------

  /**
   * Open a panel and wait for the crossfade + first paint so freshly
   * mounted DOM is queryable.
   *
   * Pointing precision (hard requirement): when a switch is needed the
   * bird VISIBLY flies to and clicks the real sidebar nav entry
   * (`.studio-sidebar-item[data-tool="<panelId>"]`) — e.g. "change
   * locations" → fly to + click the Location entry, THEN act — instead of
   * silently calling `panelManager.open`. We fall back to a direct open
   * only when the nav button can't be resolved (e.g. the script lives in
   * its own column, not the sidebar) so the choreography still proceeds.
   */
  private async openPanelAndSettle(panelId: StudioPanelId): Promise<void> {
    const isExternalSurface = panelId === "script_editor" || panelId === "timeline";
    const needsSwitch = isExternalSurface || this.panelManager.getCurrentPanelId() !== panelId;
    if (needsSwitch) {
      const navSelector =
        panelId === "script_editor"
          ? '[data-koko-action="open-script"]'
          : panelId === "timeline"
            ? '.studio-sidebar-toggle[data-toggle="timeline"]'
            : `.studio-sidebar-item[data-tool="${cssEscape(panelId)}"]`;
      const navButton = document.querySelector<HTMLButtonElement>(navSelector);
      if (this.actionAgent && navButton) {
        const alreadyOpen =
          (panelId === "script_editor" &&
            !document.querySelector(".studio-script-dock")?.classList.contains("is-closed")) ||
          (panelId === "timeline" &&
            document.querySelector(".studio-timeline-dock")?.getAttribute("data-state") === "expanded");
        if (!alreadyOpen) {
          await this.actionAgent.flyTo(navButton);
          await this.actionAgent.click(navButton);
        }
      } else {
        throw new Error(`navigation control for ${panelId} not found`);
      }
    }
    if (this.actionAgent) {
      await this.actionAgent.waitForPaint();
      // Precision: don't fly to a control that hasn't painted yet. Poll
      // until the target surface actually contains actionable DOM, so the
      // bird points at the real element instead of missing it and
      // silently falling back to a direct mutation. The script editor
      // lives in its own persistent column, not the swappable host.
      const rootSelector =
        panelId === "script_editor"
          ? ".studio-script-dock"
          : panelId === "timeline"
            ? ".studio-timeline-dock"
          : ".studio-panel-host";
      await this.waitForRootPaint(rootSelector);
    }
  }

  private async waitForCondition(
    predicate: () => boolean,
    description: string,
    maxFrames = 60,
  ): Promise<void> {
    for (let frame = 0; frame < maxFrames; frame += 1) {
      if (predicate()) return;
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }
    throw new Error(`timed out waiting for ${description}`);
  }

  private async flyToVisibleConfirmation(id: string): Promise<void> {
    if (!this.actionAgent) throw new Error("Koko action agent unavailable");
    await this.actionAgent.waitForPaint();
    const escaped = cssEscape(id);
    const target =
      document.querySelector<HTMLElement>(`[data-card-id="${escaped}"]`) ??
      document.querySelector<HTMLElement>(`[data-id="${escaped}"]`) ??
      document.querySelector<HTMLElement>(`[data-frame-id="${escaped}"]`);
    if (!target) throw new Error(`visible confirmation for ${id} did not paint`);
    await this.actionAgent.flyTo(target);
  }

  /**
   * Resolve once `rootSelector` has painted at least one tagged
   * `[data-koko-action]` control (or the frame budget runs out). Keeps
   * fly-and-point landing on present elements after a panel switch /
   * re-render.
   */
  private async waitForRootPaint(
    rootSelector: string,
    maxFrames = 48,
  ): Promise<void> {
    for (let frame = 0; frame < maxFrames; frame += 1) {
      const host = document.querySelector(rootSelector);
      if (host && host.querySelector("[data-koko-action]")) return;
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve()),
      );
    }
  }

  /**
   * Find the studio element tagged with `data-koko-action="$key"`.
   * Returns null when missing — callers throw and fall back.
   */
  private queryAction<T extends HTMLElement>(key: string): T | null {
    return document.querySelector<T>(`[data-koko-action="${cssEscape(key)}"]`);
  }

  /**
   * Same as `queryAction` but scoped to a particular id (the
   * `data-id` attribute on the element). Used for per-card
   * controls (Edit / Save / Delete on a specific character or
   * asset card).
   */
  private queryActionForId<T extends HTMLElement>(
    key: string,
    id: string,
  ): T | null {
    return document.querySelector<T>(
      `[data-koko-action="${cssEscape(key)}"][data-id="${cssEscape(id)}"]`,
    );
  }

  /** Resolve the active shared modal for a specific semantic surface. */
  private queryOpenModal(surface: string): HTMLElement | null {
    return document.querySelector<HTMLElement>(
      `.studio-modal-root[data-open="true"][data-koko-surface="${cssEscape(surface)}"]`,
    );
  }

  /**
   * Resolve a field or footer action inside the exact active modal. This
   * avoids duplicate hidden controls in the reusable inner forms and keeps
   * Koko pointing at the same visible Save button a person would click.
   */
  private queryModalActionForId<T extends HTMLElement>(
    modalRoot: HTMLElement,
    key: string,
    id: string,
  ): T | null {
    return modalRoot.querySelector<T>(
      `[data-koko-action="${cssEscape(key)}"][data-id="${cssEscape(id)}"]`,
    );
  }

  private queryModalFooterActionForId<T extends HTMLElement>(
    modalRoot: HTMLElement,
    key: string,
    id: string,
  ): T | null {
    return modalRoot.querySelector<T>(
      `.studio-modal-footer [data-koko-action="${cssEscape(key)}"][data-id="${cssEscape(id)}"]`,
    );
  }

  private warnInvalid(block: ToolUseBlock, reason: string): void {
    console.warn(`[studio] tool ${block.name} ignored — ${reason}`, block.input);
  }

  private rejectUnsupportedTool(block: ToolUseBlock, reason: string): void {
    console.warn(`[studio] tool ${block.name} refused — ${reason}`);
    this.emitChoreographyEvent("failed", describeTool(block), reason);
  }

  private choreographyFailed(action: string, cause: unknown): never {
    const reason = cause instanceof Error ? cause.message : String(cause);
    throw new Error(`${action} did not complete visibly: ${reason}`);
  }

  /**
   * Record that a choreography step completed. The message is
   * collected into `actionsCollectedThisTurn` and ultimately
   * shipped to `onTurnComplete` after the queue drains, where the
   * host turns it into a "Koko reports back" follow-up Claude
   * call. We no longer flash pills here.
   */
  private fireApplied(message: string): void {
    console.info(`[studio] ${message}`);
    this.actionsCollectedThisTurn.push(message);
  }
}

/** A short, human-readable label for the live action queue overlay. */
function describeTool(block: ToolUseBlock): string {
  const input = (block.input ?? {}) as Record<string, unknown>;
  const name = typeof input.name === "string" ? input.name.trim() : "";
  const suffix = name ? ` · ${name}` : "";
  switch (block.name) {
    case "add_character": return `Add character${suffix}`;
    case "update_character": return `Update character${suffix}`;
    case "delete_character": return "Delete character";
    case "add_asset": return `Add asset${suffix}`;
    case "update_asset": return `Update asset${suffix}`;
    case "delete_asset": return "Delete asset";
    case "add_location": return `Add location${suffix}`;
    case "update_location": return `Update location${suffix}`;
    case "delete_location": return "Delete location";
    case "add_scene": return "Add scene";
    case "update_scene": return "Update scene";
    case "delete_scene": return "Delete scene";
    case "set_costume": return "Set costume";
    case "cast_scene": return "Cast scene";
    case "set_storyboard_status": return "Set storyboard";
    case "update_sound_design": return "Update sound";
    case "set_scene_camera": return "Set camera";
    case "jump_timeline_track": return "Jump timeline";
    case "add_storyboard_shot": return "Add storyboard shot";
    case "render_final_cut": return "Render final cut";
    case "open_inline_reference": return "Open inline reference";
    case "edit_surface_image": return "Edit surface image";
    case "set_project_title": return "Rename project";
    default: return block.name.replace(/_/g, " ");
  }
}

function readString(
  input: Record<string, unknown>,
  key: string,
): string | null {
  const value = input[key];
  if (typeof value === "string") return value;
  // Permissively coerce numbers/booleans — Claude occasionally swaps types.
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

function readBoolean(
  input: Record<string, unknown>,
  key: string,
): boolean | null {
  const value = input[key];
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
}

/**
 * Minimal CSS.escape polyfill for the `[data-koko-action="..."]`
 * attribute selectors. Studio IDs are simple `kind_baseN` strings
 * but we sanitize anyway in case the LLM hands back something with
 * unusual characters. Falls through to native CSS.escape when
 * available.
 */
function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
}
